import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtMoney } from "../lib/format";
import { Card, SectionTitle } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Store, Pencil, HandCoins, Layers, Save, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { TabCobranza, PlanPagosModal, ImportadorIA, SituacionChip } from "../components/ComercialCobranza";

const ESTATUS = ["Disponible", "Apartada", "Vendida", "Bloqueada"];
const TIPOS = ["Departamento", "Lote", "Villa", "Casa", "Local comercial", "Llave hotelera", "Otro"];
const ESTATUS_STYLE = {
  Disponible: "bg-slate-100 text-slate-600",
  Apartada: "bg-amber-50 text-amber-700",
  Vendida: "bg-emerald-50 text-emerald-700",
  Bloqueada: "bg-red-50 text-red-700",
};

function NumInput({ label, value, onChange, step = "any" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}

function UnidadModal({ open, onClose, pid, unidad, upes, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF(unidad ? { ...unidad } : { clave: "", tipo: "Departamento", m2: "", precio_lista: "", upe_id: null, notas: "" });
  }, [unidad, open]);
  const save = async (e) => {
    e.preventDefault();
    const payload = { clave: f.clave || "", tipo: f.tipo || "Departamento", m2: Number(f.m2) || 0,
      precio_lista: Number(f.precio_lista) || 0, upe_id: f.upe_id || null, notas: f.notas || "" };
    try {
      if (unidad) await api.patch(`/comercial/unidades/${unidad.id}`, payload);
      else await api.post(`/properties/${pid}/comercial/unidades`, payload);
      toast.success(unidad ? "Unidad actualizada" : "Unidad creada");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="unidad-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">{unidad ? "Editar unidad" : "Nueva unidad"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Clave *</Label>
              <Input data-testid="unidad-clave-input" value={f.clave || ""} onChange={(e) => setF({ ...f, clave: e.target.value })} className="mt-1" required />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={f.tipo || "Departamento"} onValueChange={(v) => setF({ ...f, tipo: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <NumInput label="Superficie (m²)" value={f.m2} onChange={(v) => setF({ ...f, m2: v })} />
            <NumInput label="Precio de lista ($)" value={f.precio_lista} onChange={(v) => setF({ ...f, precio_lista: v })} />
          </div>
          <div>
            <Label className="text-xs">UPE vinculada (opcional)</Label>
            <Select value={f.upe_id || "none"} onValueChange={(v) => setF({ ...f, upe_id: v === "none" ? null : v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Sin UPE —</SelectItem>
                {upes.map((u) => <SelectItem key={u.id} value={u.id}>{u.clave}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea value={f.notas || ""} onChange={(e) => setF({ ...f, notas: e.target.value })} className="mt-1" rows={2} />
          </div>
          <Button data-testid="unidad-guardar-button" type="submit" className="w-full bg-[#002B49] hover:bg-[#00395f]">Guardar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VentaModal({ open, onClose, unidad, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (unidad) setF({ estatus: unidad.estatus, cliente_nombre: unidad.cliente_nombre || "",
      cliente_contacto: unidad.cliente_contacto || "", precio_venta: unidad.precio_venta ?? unidad.precio_lista ?? "",
      fecha: new Date().toISOString().slice(0, 10), notas: "" });
  }, [unidad, open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/comercial/unidades/${unidad.id}/estatus`, {
        estatus: f.estatus, cliente_nombre: f.cliente_nombre, cliente_contacto: f.cliente_contacto,
        precio_venta: f.precio_venta === "" ? null : Number(f.precio_venta), fecha: f.fecha, notas: f.notas,
      });
      toast.success("Estatus comercial actualizado");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  const conCliente = f.estatus === "Apartada" || f.estatus === "Vendida";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="venta-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Operación comercial — {unidad?.clave}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Estatus</Label>
              <Select value={f.estatus} onValueChange={(v) => setF({ ...f, estatus: v })}>
                <SelectTrigger data-testid="venta-estatus-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fecha</Label>
              <Input type="date" value={f.fecha || ""} onChange={(e) => setF({ ...f, fecha: e.target.value })} className="mt-1" />
            </div>
          </div>
          {conCliente && (
            <>
              <div>
                <Label className="text-xs">Cliente</Label>
                <Input data-testid="venta-cliente-input" value={f.cliente_nombre || ""} onChange={(e) => setF({ ...f, cliente_nombre: e.target.value })} className="mt-1" placeholder="Nombre del comprador" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Contacto</Label>
                  <Input value={f.cliente_contacto || ""} onChange={(e) => setF({ ...f, cliente_contacto: e.target.value })} className="mt-1" placeholder="Tel / correo" />
                </div>
                <NumInput label="Precio de venta ($)" value={f.precio_venta} onChange={(v) => setF({ ...f, precio_venta: v })} />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea value={f.notas || ""} onChange={(e) => setF({ ...f, notas: e.target.value })} className="mt-1" rows={2} />
          </div>
          <Button data-testid="venta-guardar-button" type="submit" className="w-full bg-[#002B49] hover:bg-[#00395f]">Aplicar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PagosModal({ open, onClose, unidad, onSaved, canWrite }) {
  const [f, setF] = useState({ fecha: new Date().toISOString().slice(0, 10), monto: "", metodo: "Transferencia", nota: "" });
  const pagos = unidad?.pagos || [];
  const total = pagos.reduce((s, p) => s + (p.monto || 0), 0);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/comercial/unidades/${unidad.id}/pagos`, { ...f, monto: Number(f.monto) || 0 });
      toast.success("Pago registrado");
      setF({ fecha: new Date().toISOString().slice(0, 10), monto: "", metodo: "Transferencia", nota: "" });
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (p) => {
    if (!window.confirm("¿Eliminar este pago?")) return;
    try { await api.delete(`/comercial/unidades/${unidad.id}/pagos/${p.id}`); onSaved(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="pagos-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Cobranza — {unidad?.clave}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Cobrado: <span className="font-bold text-[#002B49]">{fmtMoney(total)}</span>
          {unidad?.precio_venta ? <> de {fmtMoney(unidad.precio_venta)}</> : null}</p>
        <div className="max-h-44 overflow-y-auto space-y-1">
          {pagos.length === 0 && <p className="text-xs text-slate-400">Sin pagos registrados</p>}
          {pagos.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
              <span className="flex-1">{fmtDate(p.fecha)} · {p.metodo}{p.nota ? ` · ${p.nota}` : ""}</span>
              <span className="font-bold">{fmtMoney(p.monto)}</span>
              {canWrite && <button onClick={() => del(p)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
        {canWrite && (
          <form onSubmit={add} className="grid grid-cols-2 gap-2 border-t pt-3">
            <Input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} required />
            <Input data-testid="pago-monto-input" type="number" step="any" placeholder="Monto $" value={f.monto} onChange={(e) => setF({ ...f, monto: e.target.value })} required />
            <Input placeholder="Método" value={f.metodo} onChange={(e) => setF({ ...f, metodo: e.target.value })} />
            <Input placeholder="Nota" value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} />
            <Button data-testid="pago-guardar-button" type="submit" className="col-span-2 bg-[#002B49] hover:bg-[#00395f]"><Plus size={14} className="mr-1" /> Registrar pago</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiMini({ label, value, testId }) {
  return (
    <Card className="p-4" testId={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <p className="font-display text-xl font-bold text-[#002B49] mt-1">{value}</p>
    </Card>
  );
}

const PLAN_CAMPOS = [
  ["fecha_inicio", "Inicio del plan", "date"], ["fecha_fin", "Fin del plan", "date"],
  ["presupuesto_ventas", "Presupuesto de ventas ($)"], ["unidades_totales", "Unidades totales (plan)"],
  ["precio_m2_plan", "Precio objetivo / m² ($)"], ["m2_construccion", "m² de construcción"],
  ["costo_total_plan", "Costo construcción total plan ($)"], ["presupuesto_gastos", "Presupuesto de gastos ($)"],
  ["utilidad_plan", "Utilidad plan ($, vacío = auto)"], ["irr_plan", "IRR plan (%)"],
  ["capital_invertido", "Capital invertido ($)"], ["caja_plan", "Caja plan ($)"],
  ["avance_programado_pct", "Avance programado de obra (%)"],
];
const REAL_CAMPOS = [
  ["costo_construccion_real", "Costo construcción real acumulado ($)"], ["gasto_real", "Gasto real acumulado ($)"],
  ["caja_disponible", "Caja disponible ($)"], ["irr_proyectada", "IRR proyectada actual (%)"],
];

function PlanNegocio({ pid, canPlan }) {
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/properties/${pid}/plan-negocio`).then((r) => setPlan(r.data)); }, [pid]);
  if (!plan) return <p className="text-sm text-slate-400">Cargando...</p>;
  const set = (k, v) => setPlan({ ...plan, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      [...PLAN_CAMPOS, ...REAL_CAMPOS].forEach(([k, , t]) => {
        if (t === "date") payload[k] = plan[k] || null;
        else if (k === "utilidad_plan") payload[k] = plan[k] === "" || plan[k] == null ? null : Number(plan[k]);
        else payload[k] = Number(plan[k]) || 0;
      });
      payload.notas = plan.notas || "";
      const r = await api.put(`/properties/${pid}/plan-negocio`, payload);
      setPlan(r.data);
      toast.success("Plan de negocio guardado");
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const campo = ([k, label, t]) => (
    <div key={k}>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`plan-${k}-input`} type={t === "date" ? "date" : "number"} step="any" disabled={!canPlan}
        value={plan[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
    </div>
  );
  return (
    <form onSubmit={save} className="space-y-5 max-w-3xl">
      <Card className="p-5">
        <SectionTitle>Business Plan aprobado</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{PLAN_CAMPOS.map(campo)}</div>
      </Card>
      <Card className="p-5">
        <SectionTitle>Datos reales (captura manual)</SectionTitle>
        <p className="text-xs text-slate-400 mb-3">Las ventas, absorción, precio/m² y avance físico se calculan automáticamente desde Comercialización y Construcción.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{REAL_CAMPOS.map(campo)}</div>
        <div className="mt-3">
          <Label className="text-xs">Notas</Label>
          <Textarea value={plan.notas || ""} disabled={!canPlan} onChange={(e) => set("notas", e.target.value)} className="mt-1" rows={2} />
        </div>
      </Card>
      {canPlan && <Button data-testid="plan-guardar-button" type="submit" disabled={saving} className="bg-[#002B49] hover:bg-[#00395f]"><Save size={14} className="mr-2" /> Guardar plan de negocio</Button>}
      {!canPlan && <p className="text-xs text-slate-400">Solo Administrador o Financiero pueden editar el plan de negocio.</p>}
    </form>
  );
}

export default function Comercializacion() {
  const { pid } = useParams();
  const { user, isAdmin } = useAuth();
  const canWrite = ["admin", "financiero", "gestor"].includes(user?.role);
  const canPlan = ["admin", "financiero"].includes(user?.role);
  const [tab, setTab] = useState("inventario");
  const [unidades, setUnidades] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [upes, setUpes] = useState([]);
  const [filtro, setFiltro] = useState("todos");
  const [modal, setModal] = useState(null); // {tipo, unidad}

  const load = () => {
    api.get(`/properties/${pid}/comercial/unidades`).then((r) => setUnidades(r.data));
    api.get(`/properties/${pid}/comercial/resumen`).then((r) => setResumen(r.data));
  };
  useEffect(() => {
    load();
    api.get(`/properties/${pid}/upes`).then((r) => setUpes(r.data));
  }, [pid]); // eslint-disable-line

  const generarDesdeUpes = async () => {
    try {
      const r = await api.post(`/properties/${pid}/comercial/unidades/desde-upes`);
      toast.success(r.data.creadas > 0 ? `${r.data.creadas} unidades generadas desde UPEs` : "Todas las UPEs ya tienen unidad comercial");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar la unidad ${u.clave}?`)) return;
    try { await api.delete(`/comercial/unidades/${u.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const visibles = filtro === "todos" ? unidades : unidades.filter((u) => u.estatus === filtro);
  const modalUnidad = modal?.unidad ? unidades.find((x) => x.id === modal.unidad.id) || modal.unidad : null;

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Módulo Comercialización</p>
      <h1 className="font-display text-3xl font-bold text-[#002B49] mb-4">Ventas e inventario comercial</h1>

      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {[["inventario", "Inventario"], ["cobranza", "Cobranza"], ["resumen", "Resumen de ventas"], ["plan", "Plan de Negocio"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#00A3B5] text-[#002B49]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "inventario" && (
        <>
          {resumen && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <KpiMini label="Ventas acumuladas" value={fmtMoney(resumen.ventas_acumuladas)} testId="kpi-ventas" />
              <KpiMini label="Absorción" value={`${resumen.absorcion_pct}%`} testId="kpi-absorcion" />
              <KpiMini label="Vendidas / Total" value={`${resumen.vendidas} / ${resumen.total}`} testId="kpi-unidades" />
              <KpiMini label="Precio prom. / m²" value={resumen.precio_prom_m2 ? fmtMoney(resumen.precio_prom_m2) : "—"} testId="kpi-precio-m2" />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {canWrite && (
              <>
                <Button data-testid="nueva-unidad-button" onClick={() => setModal({ tipo: "unidad", unidad: null })} className="bg-[#002B49] hover:bg-[#00395f]">
                  <Plus size={14} className="mr-1" /> Nueva unidad
                </Button>
                <Button data-testid="generar-upes-button" variant="outline" onClick={generarDesdeUpes}>
                  <Layers size={14} className="mr-1" /> Generar desde UPEs
                </Button>
                <ImportadorIA pid={pid} onDone={load} />
              </>
            )}
            <div className="ml-auto w-40">
              <Select value={filtro} onValueChange={setFiltro}>
                <SelectTrigger data-testid="filtro-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {ESTATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Card className="overflow-x-auto" testId="tabla-unidades">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b">
                  <th className="px-4 py-3">Clave</th><th className="px-3 py-3">Tipo</th><th className="px-3 py-3 text-right">m²</th>
                  <th className="px-3 py-3 text-right">Precio lista</th><th className="px-3 py-3">Estatus</th>
                  <th className="px-3 py-3">Cliente</th><th className="px-3 py-3 text-right">Precio venta</th>
                  <th className="px-3 py-3 text-right">Cobrado</th><th className="px-3 py-3 text-right">Saldo</th>
                  <th className="px-3 py-3">Próximo pago</th><th className="px-3 py-3">Situación</th><th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400 text-sm">
                    <Store size={22} className="mx-auto mb-2 opacity-40" />
                    Sin unidades. Crea una unidad o genera el inventario desde las UPEs del proyecto.
                  </td></tr>
                )}
                {visibles.map((u) => (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`unidad-row-${u.clave}`}>
                    <td className="px-4 py-2.5 font-semibold text-[#002B49]">{u.clave}</td>
                    <td className="px-3 py-2.5 text-slate-500 text-xs">{u.tipo}</td>
                    <td className="px-3 py-2.5 text-right">{u.m2 || "—"}</td>
                    <td className="px-3 py-2.5 text-right">{u.precio_lista ? fmtMoney(u.precio_lista) : "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${ESTATUS_STYLE[u.estatus]}`}>{u.estatus}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs">{u.cliente_nombre || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold">{u.precio_venta ? fmtMoney(u.precio_venta) : "—"}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700">{u.pagado ? fmtMoney(u.pagado) : "—"}</td>
                    <td className="px-3 py-2.5 text-right">{u.cobranza?.saldo && u.estatus !== "Disponible" ? fmtMoney(u.cobranza.saldo) : "—"}</td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">{u.cobranza?.proximo_pago ? <>{fmtMoney(u.cobranza.proximo_pago.monto)}<span className="text-slate-400"> · {fmtDate(u.cobranza.proximo_pago.fecha)}</span></> : "—"}</td>
                    <td className="px-3 py-2.5"><SituacionChip cobranza={u.cobranza} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        {canWrite && (
                          <>
                            <button data-testid={`venta-${u.clave}`} title="Operación de venta" onClick={() => setModal({ tipo: "venta", unidad: u })} className="p-1.5 text-slate-400 hover:text-[#00A3B5]"><HandCoins size={15} /></button>
                            <button data-testid={`planpagos-${u.clave}`} title="Plan de pagos y calendario" onClick={() => setModal({ tipo: "planpagos", unidad: u })} className="p-1.5 text-slate-400 hover:text-[#002B49]"><CalendarClock size={15} /></button>
                            <button data-testid={`pagos-${u.clave}`} title="Cobranza / pagos" onClick={() => setModal({ tipo: "pagos", unidad: u })} className="p-1.5 text-slate-400 hover:text-emerald-600">$</button>
                            <button data-testid={`editar-${u.clave}`} title="Editar" onClick={() => setModal({ tipo: "unidad", unidad: u })} className="p-1.5 text-slate-400 hover:text-[#002B49]"><Pencil size={14} /></button>
                          </>
                        )}
                        {isAdmin && <button data-testid={`eliminar-${u.clave}`} title="Eliminar" onClick={() => eliminar(u)} className="p-1.5 text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "cobranza" && <TabCobranza pid={pid} canWrite={canWrite} />}

      {tab === "resumen" && resumen && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiMini label="Ventas acumuladas" value={fmtMoney(resumen.ventas_acumuladas)} />
            <KpiMini label="Absorción" value={`${resumen.absorcion_pct}%`} />
            <KpiMini label="Precio prom. / m²" value={resumen.precio_prom_m2 ? fmtMoney(resumen.precio_prom_m2) : "—"} />
            <KpiMini label="Cobrado" value={fmtMoney(resumen.cobrado)} />
            <KpiMini label="Por cobrar" value={fmtMoney(resumen.por_cobrar)} />
            <KpiMini label="Inventario disponible" value={fmtMoney(resumen.valor_inventario_disponible)} />
          </div>
          <Card className="p-5" testId="grafica-evolucion">
            <SectionTitle>Evolución mensual de ventas</SectionTitle>
            {resumen.evolucion.length === 0 ? (
              <p className="text-sm text-slate-400">Aún no hay ventas registradas.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={resumen.evolucion}>
                  <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={70} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                  <RTooltip formatter={(v, n) => [fmtMoney(v), n === "monto" ? "Ventas del mes" : "Acumulado"]} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="monto" fill="#00A3B5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Vendidas", resumen.vendidas, "text-emerald-600"], ["Apartadas", resumen.apartadas, "text-amber-600"],
              ["Disponibles", resumen.disponibles, "text-slate-500"], ["Bloqueadas", resumen.bloqueadas, "text-red-500"]].map(([l, v, c]) => (
              <Card key={l} className="p-4 text-center">
                <p className={`font-display text-2xl font-bold ${c}`}>{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{l}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "plan" && <PlanNegocio pid={pid} canPlan={canPlan} />}

      <UnidadModal open={modal?.tipo === "unidad"} onClose={() => setModal(null)} pid={pid} unidad={modal?.unidad} upes={upes} onSaved={load} />
      <VentaModal open={modal?.tipo === "venta"} onClose={() => setModal(null)} unidad={modalUnidad} onSaved={load} />
      <PagosModal open={modal?.tipo === "pagos"} onClose={() => setModal(null)} unidad={modalUnidad} onSaved={load} canWrite={canWrite} />
      <PlanPagosModal open={modal?.tipo === "planpagos"} onClose={() => setModal(null)} unidad={modalUnidad} onSaved={load} canWrite={canWrite} />
    </div>
  );
}
